import styled from '@emotion/styled';
import Media from '@ep/components/Media';
import theme from '@ep/theme';
import { Gcms_Asset, Gcms_RichText } from '@ep/types/gatsbyTypes';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Modal, { ModalProps } from '@mui/material/Modal';
import 'lazysizes';
import React from 'react';

type Directions = 'left' | 'center' | 'right';
type Layouts = 'row' | 'column';

const directionMap = {
  left: 'start',
  right: 'end',
  center: 'center',
};
export interface ContentLightBoxProps {
  openLightBox: boolean;
  setOpenLightBox: (state: boolean) => void;
  contentLightBoxText?: Gcms_RichText;
  contentLightBoxImg?: Gcms_Asset;
  direction?: Directions;
  fullWidth?: boolean;
  layout?: Layouts;
  children?: unknown;
}

export interface VideoProps {
  hasVideo: boolean;
}

const ContentLightBoxText = styled.div`
  overflow-y: auto;
  max-height: 30rem;
  padding: 0.8rem 1rem;
  ul {
    padding: 0;
    margin: 0;
    li {
      list-style: none;
      padding-left: 1rem;
      position: relative;
      &:before {
        content: '>';
        position: absolute;
        left: 0;
        top: 0;
        color: ${theme.colors.oceanBlue};
      }
    }
`;
interface StyledModalProps extends ModalProps {
  direction: Directions;
  open: boolean;
  onClose: () => void;
}

const StyledModal = styled(Modal)<StyledModalProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${({ direction }) => directionMap[direction]};
  margin: ${({ direction }) =>
    direction == 'center' ? '2rem auto' : 'auto 2rem'};
`;

const CloseModal = styled.div`
  position: relative;
  z-index: 3;
`;

interface ModalContainerProps {
  fullWidth: boolean;
  layout: Layouts;
}

const ModalContainer = styled.div<ModalContainerProps>`
  position: relative;
  max-height: 95vh;
  background-color: ${theme.colors.white};
  outline: none;
  border-radius: 1rem;
  display: flex;
  flex-direction: ${({ layout }) => layout};
  width: ${({ fullWidth }) => (fullWidth ? '90%' : '48rem')};
  max-width: 90%;
  margin: 0 auto;
  overflow: hidden;
  ${theme.mediaQueries.tablet} {
    width: 65%;
  }
  ${theme.mediaQueries.mobile} {
    width: 90%;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  position: absolute;
  right: 0;
  top: 0;
`;

const VideoWrapper = styled.div`
  min-width: 20rem;
  max-width: 100%;
  margin: 0 auto;
`;

const StyledMedia = styled(Media)`
  min-width: 40rem;
  img {
    max-height: 30rem;
    width: 48rem;
    border-radius: 1rem 1rem 0 0;
  }
  ${theme.mediaQueries.tablet} {
    min-width: 36rem;
    img {
      width: 40rem;
    }
  }
  ${theme.mediaQueries.mobile} {
    min-width: 22rem;
    img {
      width: 24rem;
    }
  }
`;
const CaptionWrapper = styled.div`
  padding: 1rem 2rem;
  overflow-y: auto;
`;
const ChildrenWrapper = styled.div`
  position: relative;
  padding: 1rem 2rem;
  ${theme.mediaQueries.mobileAndTablet} {
    padding: 1rem;
  }
`;
const StyledIconButton = styled(IconButton)`
  background-olor: ${theme.colors.white85};
  border: 1.8px solid ${theme.colors.epBlack};
  padding: 5px;
`;

const ContentLightBox = ({
  openLightBox,
  setOpenLightBox,
  contentLightBoxText,
  contentLightBoxImg,
  direction = 'center',
  layout = 'column',
  fullWidth = false,
  children,
}: ContentLightBoxProps) => {
  return (
    <StyledModal
      open={openLightBox}
      onClose={() => setOpenLightBox(false)}
      direction={direction}
    >
      <ModalContainer layout={layout} fullWidth={fullWidth}>
        <ModalHeader>
          <CloseModal>
            <StyledIconButton
              aria-label="close"
              onClick={() => setOpenLightBox(false)}
            >
              <CloseIcon htmlColor={theme.colors.black} />
            </StyledIconButton>
          </CloseModal>
        </ModalHeader>
        {contentLightBoxImg && (
          <VideoWrapper>
            <StyledMedia
              media={
                {
                  url: contentLightBoxImg.url,
                  vidyardLink: contentLightBoxImg.vidyardLink,
                } as Gcms_Asset
              }
            />
          </VideoWrapper>
        )}
        {contentLightBoxText && (
          <CaptionWrapper>
            <ContentLightBoxText
              dangerouslySetInnerHTML={{ __html: contentLightBoxText.html }}
            />
          </CaptionWrapper>
        )}

        {children && <ChildrenWrapper>{children} </ChildrenWrapper>}
      </ModalContainer>
    </StyledModal>
  );
};
export default ContentLightBox;
